// Security (/settings/security): the identity-protection controls, grouped
// away from the profile fields in Account — passkeys (add/remove), the account
// password (2FA), and the recovery phrase. All are server-backed except the
// phrase (local), so an unregistered device (no userId) is bounced back to
// Settings, matching Account.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { MeResponse } from '@merkury/protocol';
import { useAccount } from '../../shell/accountContext.js';
import { useT } from '../../i18n/index.js';
import { useScreens } from '../ScreensContext.js';
import { PasskeysSection } from './PasskeysSection.js';
import { PasswordSection, PhraseSection } from './AccountSettingsScreen.js';
import { SettingsSubScreen } from './chrome.js';

export function SecuritySettingsScreen(): ReactNode {
  const t = useT();
  const { state } = useAccount();
  const { api } = useScreens();
  const [me, setMe] = useState<MeResponse | null>(null);

  const reloadMe = useCallback(async () => {
    const res = await api.getMe().catch(() => null);
    if (res) setMe(res);
  }, [api]);

  useEffect(() => {
    void reloadMe();
  }, [reloadMe]);

  if (state.status !== 'ready') {
    return (
      <main className="mx-auto flex min-h-full w-full max-w-app items-center justify-center p-6">
        <p className="text-sm text-muted" role="status">
          {t('common.loading')}
        </p>
      </main>
    );
  }
  const { identity } = state;
  const userId = state.account.userId;
  if (!userId) return <Navigate to="/settings" replace />;

  return (
    <SettingsSubScreen section="security" title={t('settings.security')}>
      <PasskeysSection />
      <PasswordSection me={me} onChanged={() => void reloadMe()} />
      <PhraseSection identity={identity} />
    </SettingsSubScreen>
  );
}
